import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputBase from '@mui/material/InputBase';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { CustomerSwapQuoteResponse } from '@shared/contract';

import { useAccount } from '../account/AccountContext';
import { DemoApiError } from '../api/client';
import { useSelection } from '../selection/SelectionContext';
import { useSession } from '../session/SessionContext';
import { brand } from '../theme';

/** Symbols forced onto their Solana mint — mirrors the Lambda/Cheddar pair rule. */
const FORCED_SOLANA_SYMBOLS = new Set(['USDC', 'USDT']);

interface SwappableToken {
  mintId: number;
  symbol: string;
  chain: string;
  decimals: number;
  usdPrice: string;
}

function pairAllowed(from: SwappableToken, to: SwappableToken): boolean {
  if (from.symbol === to.symbol) return false;
  if (FORCED_SOLANA_SYMBOLS.has(from.symbol) && from.chain !== 'SOLANA') return false;
  if (FORCED_SOLANA_SYMBOLS.has(to.symbol) && to.chain !== 'SOLANA') return false;
  return true;
}

function swapErrorCopy(err: unknown): string {
  if (err instanceof DemoApiError) {
    switch (err.code) {
      case 'SWAPS_DISABLED':
        return 'Token swaps are not enabled for this demo org yet.';
      case 'INVALID_PAIR':
        return 'That pair cannot be swapped. Pick a different currency or network.';
      case 'BELOW_MINIMUM':
        return 'Amount is below the minimum Cheddar will swap.';
      case 'INSUFFICIENT_LIQUIDITY':
        return 'Not enough liquidity right now — try a smaller amount or another token.';
      case 'QUOTE_UNAVAILABLE':
        return 'Could not get a quote right now. Try again in a moment.';
      case 'QUOTE_REJECTED':
        // Permanent for this amount/pair — no "try again" copy here, unlike
        // QUOTE_UNAVAILABLE above. The caller must change the amount or pair.
        return err.providerMessage
          ? `This swap was rejected: ${err.providerMessage}`
          : "This pair or amount can't be swapped right now.";
      case 'INSUFFICIENT_DEMO_BALANCE':
        return err.message;
      case 'SWAP_SUBMIT_AMBIGUOUS':
        return "We couldn't confirm whether this swap went through. Don't submit it again — check the activity list in a moment; if it didn't go through, your balance is refunded automatically.";
      default:
        return err.message;
    }
  }
  return err instanceof Error ? err.message : 'Swap failed';
}

function decimalPlaces(value: string): number {
  const idx = value.indexOf('.');
  return idx === -1 ? 0 : value.length - idx - 1;
}

const QUOTE_DEBOUNCE_MS = 450;

export interface CustomerSwapPanelProps {
  /** When true, omit the outer card shell (parent already provides one). */
  embedded?: boolean;
}

export default function CustomerSwapPanel({ embedded = false }: CustomerSwapPanelProps) {
  const { session, api } = useSession();
  const { networks } = useSelection();
  const { account } = useAccount();

  const tokens: SwappableToken[] = useMemo(() => {
    const rows: SwappableToken[] = [];
    for (const group of networks) {
      for (const token of group.tokens) {
        if (!token.swappable) continue;
        rows.push({
          mintId: token.mintId,
          symbol: token.symbol,
          chain: group.chain,
          decimals: token.decimals,
          usdPrice: token.usdPrice,
        });
      }
    }
    return rows;
  }, [networks]);

  const fromOptions = useMemo(() => {
    return tokens.filter((t) => {
      const balance = account?.customerBalances[String(t.mintId)];
      return balance != null && Number(balance) > 0;
    });
  }, [tokens, account]);

  const [fromMintId, setFromMintId] = useState<number | ''>('');
  const [toMintId, setToMintId] = useState<number | ''>('');
  const [amount, setAmount] = useState('');
  const [clientError, setClientError] = useState<string | null>(null);
  const [quote, setQuote] = useState<CustomerSwapQuoteResponse | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<{
    amount: string;
    fromSymbol: string;
    toSymbol: string;
    estimatedTimeSeconds: number | null;
  } | null>(null);
  // Set instead of `submitted` when the submit call comes back ambiguous
  // (5xx/timeout — the swap may or may not have been created). Blocks the
  // normal form so the user can't blindly hit "Swap" again and fire a
  // second, real, swap; they can only acknowledge and check status instead.
  const [ambiguous, setAmbiguous] = useState<{ amount: string; fromSymbol: string } | null>(
    null,
  );

  const fromToken = fromOptions.find((t) => t.mintId === fromMintId) ?? null;
  const toOptions = useMemo(() => {
    if (!fromToken) return [];
    return tokens.filter((t) => t.mintId !== fromToken.mintId && pairAllowed(fromToken, t));
  }, [tokens, fromToken]);
  const toToken = toOptions.find((t) => t.mintId === toMintId) ?? null;

  const balance =
    fromToken && account ? (account.customerBalances[String(fromToken.mintId)] ?? '0') : '0';

  // Reset the destination + amount whenever the source changes so a stale
  // pair or an amount that no longer fits the new mint's decimals can't submit.
  useEffect(() => {
    setToMintId('');
    setAmount('');
    setClientError(null);
    setQuote(null);
    setQuoteError(null);
  }, [fromMintId]);

  useEffect(() => {
    setAmount('');
    setClientError(null);
    setQuote(null);
    setQuoteError(null);
  }, [toMintId]);

  const seq = useRef(0);

  useEffect(() => {
    if (!session || !fromToken || !toToken || !amount.trim()) {
      setQuote(null);
      setQuoteError(null);
      return;
    }
    if (Number.isNaN(Number(amount)) || Number(amount) <= 0) {
      setQuote(null);
      return;
    }
    if (decimalPlaces(amount) > fromToken.decimals) {
      setQuote(null);
      return;
    }

    const mySeq = ++seq.current;
    const timer = window.setTimeout(() => {
      setQuoteLoading(true);
      setQuoteError(null);
      api
        .quoteCustomerSwap({
          customerId: session.customerId,
          fromMintId: fromToken.mintId,
          toMintId: toToken.mintId,
          amount: amount.trim(),
        })
        .then((res) => {
          if (mySeq !== seq.current) return;
          setQuote(res);
        })
        .catch((err: unknown) => {
          if (mySeq !== seq.current) return;
          setQuote(null);
          setQuoteError(swapErrorCopy(err));
        })
        .finally(() => {
          if (mySeq === seq.current) setQuoteLoading(false);
        });
    }, QUOTE_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, fromToken?.mintId, toToken?.mintId, amount]);

  if (!session) return null;

  const validateClient = (): string | null => {
    if (!fromToken) return 'Select a token to swap from';
    if (!toToken) return 'Select a token to swap to';
    if (!amount.trim()) return 'Enter an amount';
    const n = Number(amount);
    if (Number.isNaN(n) || n <= 0) return 'Amount must be a positive number';
    if (decimalPlaces(amount) > fromToken.decimals) {
      return `${fromToken.symbol} supports at most ${fromToken.decimals} decimal places`;
    }
    if (n > Number(balance)) {
      return `Amount exceeds available balance (${balance} ${fromToken.symbol})`;
    }
    return null;
  };

  const handleMax = () => {
    if (!fromToken) return;
    setAmount(balance);
    setClientError(null);
  };

  const handleSubmit = async () => {
    const err = validateClient();
    if (err) {
      setClientError(err);
      return;
    }
    if (!fromToken || !toToken) return;
    setClientError(null);
    setSubmitError(null);
    setSubmitting(true);
    try {
      await api.createCustomerSwap({
        customerId: session.customerId,
        fromMintId: fromToken.mintId,
        toMintId: toToken.mintId,
        amount: amount.trim(),
      });
      setSubmitted({
        amount: amount.trim(),
        fromSymbol: fromToken.symbol,
        toSymbol: toToken.symbol,
        estimatedTimeSeconds: quote?.estimatedTimeSeconds ?? null,
      });
      setAmount('');
      setQuote(null);
    } catch (e) {
      if (e instanceof DemoApiError && e.code === 'SWAP_SUBMIT_AMBIGUOUS') {
        // Ambiguous outcome — do not leave the ordinary form (and its "Swap"
        // button) up, or the user could resubmit and create a second real
        // swap. Clear the amount too, so there's nothing left to resend.
        setAmbiguous({ amount: amount.trim(), fromSymbol: fromToken.symbol });
        setAmount('');
        setQuote(null);
      } else {
        setSubmitError(swapErrorCopy(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (ambiguous) {
    return (
      <Box data-testid={embedded ? 'customer-swap-panel' : 'customer-swap-card'}>
        <Stack alignItems="center" spacing={1.5} sx={{ textAlign: 'center', py: 1.5 }}>
          <Box
            component="span"
            aria-hidden
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              backgroundColor: brand.goldWash,
              color: brand.goldDeep,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              fontWeight: 800,
            }}
          >
            ?
          </Box>
          <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>
            Couldn&apos;t confirm the swap
          </Typography>
          <Typography
            sx={{ fontSize: 14, color: brand.muted, lineHeight: 1.55, maxWidth: 380 }}
          >
            We lost the response when swapping{' '}
            <strong style={{ color: brand.ink }}>
              {ambiguous.amount} {ambiguous.fromSymbol}
            </strong>
            . It may still be processing — check the activity list below before trying again;
            don&apos;t resubmit this swap.
          </Typography>
          <Button
            variant="contained"
            onClick={() => setAmbiguous(null)}
            sx={{ mt: 1, minWidth: 200 }}
          >
            Done
          </Button>
        </Stack>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box data-testid={embedded ? 'customer-swap-panel' : 'customer-swap-card'}>
        <Stack alignItems="center" spacing={1.5} sx={{ textAlign: 'center', py: 1.5 }}>
          <Box
            component="span"
            aria-hidden
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              backgroundColor: brand.goldWash,
              color: brand.goldDeep,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              fontWeight: 800,
              animation: 'chd-pulse 2.2s ease-in-out infinite',
            }}
          >
            ⇄
          </Box>
          <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>
            Swap in progress
          </Typography>
          <Typography
            sx={{ fontSize: 14, color: brand.muted, lineHeight: 1.55, maxWidth: 380 }}
          >
            Swapping{' '}
            <strong style={{ color: brand.ink }}>
              {submitted.amount} {submitted.fromSymbol}
            </strong>{' '}
            to <strong style={{ color: brand.ink }}>{submitted.toSymbol}</strong>.
            {submitted.estimatedTimeSeconds != null
              ? ` Usually takes about ${submitted.estimatedTimeSeconds}s.`
              : ''}{' '}
            Watch the activity list below — the destination balance updates once the swap lands.
          </Typography>
          <Button variant="contained" onClick={() => setSubmitted(null)} sx={{ mt: 1, minWidth: 200 }}>
            New swap
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box data-testid={embedded ? 'customer-swap-panel' : 'customer-swap-card'}>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.75} alignItems="stretch">
          <FormControl fullWidth size="small" disabled={fromOptions.length === 0}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: brand.body, mb: 0.5 }}>
              From
            </Typography>
            <Select
              displayEmpty
              value={fromMintId === '' ? '' : String(fromMintId)}
              onChange={(e) => setFromMintId(e.target.value === '' ? '' : Number(e.target.value))}
              inputProps={{ 'aria-label': 'Swap from' }}
              MenuProps={{ disablePortal: true }}
            >
              <MenuItem value="" disabled>
                {fromOptions.length === 0 ? 'No swappable balance' : 'Select a token'}
              </MenuItem>
              {fromOptions.map((t) => (
                <MenuItem key={t.mintId} value={String(t.mintId)}>
                  {t.symbol} · {t.chain}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" disabled={!fromToken || toOptions.length === 0}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: brand.body, mb: 0.5 }}>
              To
            </Typography>
            <Select
              displayEmpty
              value={toMintId === '' ? '' : String(toMintId)}
              onChange={(e) => setToMintId(e.target.value === '' ? '' : Number(e.target.value))}
              inputProps={{ 'aria-label': 'Swap to' }}
              MenuProps={{ disablePortal: true }}
            >
              <MenuItem value="" disabled>
                {!fromToken
                  ? 'Choose a source first'
                  : toOptions.length === 0
                    ? 'No eligible destination'
                    : 'Select a token'}
              </MenuItem>
              {toOptions.map((t) => (
                <MenuItem key={t.mintId} value={String(t.mintId)}>
                  {t.symbol} · {t.chain}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {fromToken ? (
          <Stack spacing={0.875}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: brand.body }}>
              Amount — available {balance} {fromToken.symbol}
            </Typography>
            <Box
              sx={{
                height: 52,
                borderRadius: `${brand.controlRadius}px`,
                border: `1px solid ${brand.border}`,
                backgroundColor: brand.canvasSunk,
                px: 1.75,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <InputBase
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                inputMode="decimal"
                disabled={submitting}
                sx={{ flex: 1, minWidth: 0, fontSize: 20, fontWeight: 700, color: brand.ink }}
              />
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: brand.muted }}>
                {fromToken.symbol}
              </Typography>
              <Button
                onClick={handleMax}
                disabled={submitting}
                sx={{
                  flex: 'none',
                  height: 28,
                  minWidth: 0,
                  px: 1.25,
                  borderRadius: '8px',
                  border: `1px solid ${brand.border}`,
                  backgroundColor: brand.surface,
                  fontSize: 12,
                  fontWeight: 700,
                  color: brand.goldDeep,
                  '&:hover': { backgroundColor: brand.goldWash },
                }}
              >
                MAX
              </Button>
            </Box>
          </Stack>
        ) : null}

        {quoteLoading ? (
          <Typography sx={{ fontSize: 12.5, color: brand.faint }}>Getting quote…</Typography>
        ) : quote ? (
          <Box
            sx={{
              borderRadius: `${brand.controlRadius}px`,
              border: `1px solid ${brand.hairline}`,
              backgroundColor: brand.canvasSunk,
              px: 1.75,
              py: 1.25,
            }}
          >
            <Typography sx={{ fontSize: 13, color: brand.body, fontWeight: 600 }}>
              ≈ {quote.estimatedAmountOut} {toToken?.symbol} (min {quote.minAmountOut}{' '}
              {toToken?.symbol})
            </Typography>
            <Typography sx={{ fontSize: 12, color: brand.faint, mt: 0.25 }}>
              ${quote.usdValue} USD
              {quote.estimatedTimeSeconds != null
                ? ` · ~${quote.estimatedTimeSeconds}s`
                : ''}{' '}
              — Estimate. Final amount may vary.
            </Typography>
          </Box>
        ) : quoteError ? (
          <Alert severity="warning">{quoteError}</Alert>
        ) : null}

        {clientError ? <Alert severity="warning">{clientError}</Alert> : null}
        {submitError ? <Alert severity="error">{submitError}</Alert> : null}

        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={submitting || !fromToken || !toToken || !amount.trim()}
          fullWidth
          sx={{ height: 52, fontSize: 16 }}
        >
          {submitting ? 'Submitting…' : 'Swap'}
        </Button>
      </Stack>
    </Box>
  );
}
